import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { FaqAccordion } from '../components/FaqAccordion';
export function Book() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const faqItems = [{
    question: 'What is this conversation?',
    answer: 'It is a 45-minute working conversation to pressure-test what you are seeing, where revenue may be getting stuck, lost, delayed, or missed, and whether there is a real issue or opportunity worth diagnosing. It is not a discovery call disguised as a pitch.'
  }, {
    question: 'What if the issue seems split across multiple teams?',
    answer: 'That is common. A lot of these issues cut across functions. What looks like a sales issue, an operations issue, or a customer success issue is often a breakdown in the seams between them.'
  }, {
    question: 'How do I know if this is really a revenue journey issue?',
    answer: 'Usually some version of this: the wrong customers get sold, leads stall in the middle, handoffs break, delivery inherits shaky promises, churn starts earlier than leadership thinks, pricing or packaging no longer fits cleanly, or expansion keeps getting missed.'
  }, {
    question: 'Is this only about pre-sale issues?',
    answer: 'No. The revenue journey includes what happens before and after the sale — qualification, routing, conversion, handoffs, promise-to-delivery alignment, churn drivers, retention, expansion, pricing, packaging, and the decision rules around them.'
  }, {
    question: 'What happens if the issue is real?',
    answer: 'The next step is usually a Revenue Journey Diagnostic. That is where we map where revenue is being lost, delayed, or missed, identify the highest-leverage point to address first, and decide whether redesign makes sense.'
  }, {
    question: 'What happens if the issue is not structural?',
    answer: 'Then I will tell you. One possible outcome is that you are fine and should keep pushing. Another is that something is real, but it is not the right timing yet.'
  }, {
    question: 'Do you take over execution?',
    answer: 'No. I do not become the long-term escalation path, own day-to-day throughput, or stay in the middle to keep things moving. The work is designed to reduce dependence, not shift it somewhere else.'
  }, {
    question: 'Where does AI fit into this?',
    answer: 'Usually later. AI can help when the workflow, ownership, definitions, and decision rules are clear enough to support it. When those things are still fuzzy, AI usually scales confusion instead of leverage.'
  }];
  return <main className="w-full" data-id="element-309">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-310">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-311">
          <BlueprintGrid data-id="element-312" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center relative z-10" data-id="element-313">
          <motion.p variants={fadeIn} className="eyebrow mb-6 text-seaglass" data-id="element-314">
            Book an Honest Conversation
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-315">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-316">
              A 45-minute working conversation
            </span>{' '}
            <span className="text-navy" data-id="element-317">
              to figure out what is really going on.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mx-auto mb-8 space-y-4" data-id="element-318">
            <p data-id="element-319">This is not a discovery call disguised as a pitch.</p>
            <p data-id="element-320">
              It is a working conversation to pressure-test what you are seeing,
              where revenue may be getting stuck, lost, delayed, or missed, and
              whether there is a real issue or opportunity worth diagnosing.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col items-center gap-2 mb-10" data-id="element-321">
            <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-322">
              <span data-id="element-323">Choose a time</span>
              <ChevronDown className="w-5 h-5" data-id="element-324" />
            </a>
            <p className="text-sm text-text-muted" data-id="element-325">
              Video call. You do not need to prep.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What we cover */}
      <section className="py-24 px-6 bg-paper-dark border-t hairline-top" data-id="element-326">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-327">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-328">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-329">
              What we cover
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-330">
            <p className="text-body text-text" data-id="element-331">
              We use the conversation to get sharper on:
            </p>

            <ul className="space-y-3 pl-1" data-id="element-332">
              {['what looks broken on the surface', 'where the real issue or missed opportunity may actually sit', 'whether the journey no longer fits how customers are actually moving', 'what may be getting lost before or after the sale', 'whether the issue is worth diagnosing now'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-333">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-334" />
                  <span className="text-body text-text" data-id="element-335">{item}</span>
                </li>)}
            </ul>

            <div className="mt-8 space-y-4" data-id="element-336">
              <p className="text-body text-text" data-id="element-337">
                You do not need a perfect story before the call.
              </p>
              <p className="text-body text-navy font-medium" data-id="element-338">
                You just need enough signal to look at honestly.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Best fit */}
      <section className="py-24 px-6 bg-paper" data-id="element-339">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-340">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-341">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-342">
              Best fit
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-343">
            <p className="text-body text-text" data-id="element-344">This tends to be useful when:</p>

            <ul className="space-y-3 pl-1" data-id="element-345">
              {['demand is being created, but too much revenue is still getting stuck, lost, or missed in the middle', 'sales, delivery, customer success, and operations keep creating friction for each other', 'churn, expansion, pricing, or packaging issues seem more connected than they first appear', 'too much still depends on judgment trapped in people, key employees, or cleanup', 'leadership knows the business should feel cleaner, clearer, or more commercially aligned than it does'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-346">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-347" />
                  <span className="text-body text-text" data-id="element-348">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </motion.div>
      </section>

      {/* What you leave with */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-349">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-350">
          <motion.div variants={fadeIn} className="mb-16 text-center" data-id="element-351">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-352">
              You leave with
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6" data-id="element-353">
            {[{
            title: 'Clearer read',
            body: 'A better read on what may actually be happening.'
          }, {
            title: 'Honest assessment',
            body: 'A sharper sense of whether the issue looks structural, situational, opportunistic, or not worth touching right now.'
          }, {
            title: 'Next right step',
            body: 'A real next move: act now, wait, or leave it alone.'
          }, {
            title: 'Less guessing',
            body: 'More confidence about what is worth changing, what is worth testing, and what is not.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} className="p-8 bg-white border border-seaglass/10" data-id="element-354">
                <div className="flex items-center space-x-3 mb-4" data-id="element-355">
                  <Check className="w-6 h-6 text-seaglass flex-shrink-0" data-id="element-356" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-357">
                  {item.title}
                </h3>
                <p className="text-sm text-text leading-relaxed" data-id="element-358">{item.body}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Possible outcomes */}
      <section className="py-24 px-6 bg-paper" data-id="element-359">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-360">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-361">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-362">
              Possible outcomes
            </h2>
            <p className="text-lg text-text" data-id="element-363">
              There are only a few honest answers that come out of this
              conversation:
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-364">
            <ul className="space-y-4" data-id="element-365">
              <li className="flex items-start space-x-3" data-id="element-366">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-367" />
                <span className="text-body text-text" data-id="element-368">
                  <strong className="font-semibold text-navy" data-id="element-369">
                    You are fine.
                  </strong>{' '}
                  Keep pushing.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-370">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-371" />
                <span className="text-body text-text" data-id="element-372">
                  <strong className="font-semibold text-navy" data-id="element-373">
                    Something is real here, but it is not the right timing yet.
                  </strong>{' '}
                  Wait.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-374">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-375" />
                <span className="text-body text-text" data-id="element-376">
                  <strong className="font-semibold text-navy" data-id="element-377">
                    There is a real issue or opportunity here worth diagnosing.
                  </strong>{' '}
                  Move into a Revenue Journey Diagnostic.
                </span>
              </li>
            </ul>

            <p className="text-body text-navy font-medium" data-id="element-378">
              If the issue does not look structural or worth pursuing, I will
              tell you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* If there is something real to pursue */}
      <section className="relative py-20 px-6 bg-navy grain overflow-hidden" data-id="element-379">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-380">
          <BlueprintGrid data-id="element-381" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-382">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4 leading-tight" data-id="element-383">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-384">
              If there is something real
            </span>{' '}
            <span className="text-white" data-id="element-385">to pursue</span>
          </h2>
          <p className="text-lg text-white/80 mb-4" data-id="element-386">
            The next step is usually a Revenue Journey Diagnostic.
          </p>
          <p className="text-lg text-white/70 mb-8" data-id="element-387">
            That is where we map where revenue is getting stuck, lost, delayed,
            or missed before and after the sale, identify the highest-leverage
            point to address first, and decide whether redesign makes sense.
          </p>
          <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-388">
            <span data-id="element-389">Choose a time</span>
            <ChevronDown className="w-5 h-5" data-id="element-390" />
          </a>
        </motion.div>
      </section>

      {/* Calendly */}
      <section id="calendar" className="py-24 px-6 bg-paper-dark grain" data-id="element-391">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-4xl mx-auto" data-id="element-392">
          <div className="p-8 bg-paper/30 border border-seaglass/10" data-id="element-393">
            <iframe className="bg-white w-full" src="https://calendly.com/johnwestley/honest-conversation?hide_gdpr_banner=1" title="Book an Honest Conversation" style={{
            minWidth: '320px',
            height: '700px'
          }} frameBorder="0" data-id="element-394"></iframe>
          </div>

          <p className="text-center text-sm text-text-muted mt-4" data-id="element-395">
            If the calendar does not load,{' '}
            <a href="https://calendly.com/johnwestley/honest-conversation" target="_blank" rel="noopener noreferrer" className="text-seaglass hover:text-seaglass-light underline transition-colors duration-200" data-id="element-396">
              open it here
            </a>
            .
          </p>

          <div className="mt-12 text-center space-y-4" data-id="element-397">
            <p className="text-text" data-id="element-398">Questions before booking?</p>
            <a href="mailto:john@growththatholds.com" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-399">
              <Mail className="w-4 h-4" data-id="element-400" />
              <span data-id="element-401">john@growththatholds.com</span>
            </a>
            <p className="text-sm text-text-muted" data-id="element-402">
              If we are not a fit, I will tell you quickly.
            </p>
            <div className="pt-4" data-id="element-403">
              <a href="#calendar" className="inline-flex items-center space-x-2 text-sm text-text-muted hover:text-navy transition-colors duration-300" data-id="element-404">
                <ChevronUp className="w-4 h-4" data-id="element-405" />
                <span data-id="element-406">Back to calendar</span>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-paper" data-id="element-407">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-408">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-409">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-410">
              FAQ
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-411">
            <FaqAccordion items={faqItems} data-id="element-412" />
          </motion.div>
        </motion.div>
      </section>
    </main>;
}